import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  Pagination,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  getCategoriesSagaAction,
  getTestCasesSagaAction,
  createTestCaseSagaAction,
  updateTestCaseSagaAction,
  deleteTestCaseSagaAction,
} from "../../../../../../redux/saga/project-validation/saga-actions";
import { TestCaseType } from "../../../../../../redux/slices/project-validation/Types";
import AddTestCaseModal from "./AddTestCaseModal";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import partnerValidationImage from "../../../../../../common/assets/images/partner-validation.png";
import "./TestCaseManagement.scss";

const TestCaseManagement: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const { categories, testCases, status } = useAppSelector(
    (state) => state.project_validation
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewModalType, setViewModalType] = useState<
    "precondition" | "description" | "expectedResult"
  >("precondition");
  const [selectedTestCase, setSelectedTestCase] = useState<TestCaseType | null>(
    null
  );
  const [editingTestCase, setEditingTestCase] = useState<TestCaseType | null>(
    null
  );
  const [deletingTestCaseId, setDeletingTestCaseId] = useState<string | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const catsScrollRef = React.useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(getCategoriesSagaAction());
    dispatch(getTestCasesSagaAction());
  }, [dispatch]);

  // Group test cases by category and calculate counts
  const categoriesWithCounts = useMemo(() => {
    const categoryMap = new Map();
    categories.forEach((cat) => {
      categoryMap.set(cat.ID, {
        ...cat,
        testCaseCount: testCases.filter((tc) => tc.category_id === cat.ID)
          .length,
      });
    });
    return Array.from(categoryMap.values());
  }, [categories, testCases]);

  // useEffect(() => {
  //   if (categoriesWithCounts.length > 0 && selectedCategory === null) {
  //     setSelectedCategory(categoriesWithCounts[0].ID);
  //   }
  // }, [categoriesWithCounts, selectedCategory]);

  // Filter test cases by selected category
  const filteredTestCases = useMemo(() => {
    if (!selectedCategory) return testCases;
    return testCases.filter((tc) => tc.category_id === selectedCategory);
  }, [testCases, selectedCategory]);

  // Pagination logic
  const totalRecords = filteredTestCases.length;
  const startIndex = (currentPageNumber - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTestCases = filteredTestCases.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPageNumber(page);
  };

  const onPageSizesClick = (_e: React.MouseEvent<HTMLElement>, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  // Reset pagination when category changes
  useEffect(() => {
    setCurrentPageNumber(1);
  }, [selectedCategory]);

  const handleOpenModal = () => {
    setEditingTestCase(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTestCase(null);
  };

  const handleEditTestCase = (testCase: TestCaseType) => {
    setEditingTestCase(testCase);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (testCaseId: string) => {
    setDeletingTestCaseId(testCaseId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingTestCaseId) {
      dispatch(deleteTestCaseSagaAction(deletingTestCaseId));
    }
    setIsDeleteModalOpen(false);
    setDeletingTestCaseId(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingTestCaseId(null);
  };

  const handleSaveTestCase = (testCaseData: {
    category: string;
    highLevelScenario: string;
    preConditions: string;
    description: string;
    expectedResult: string;
    status: boolean;
  }) => {
    if (editingTestCase) {
      dispatch(
        updateTestCaseSagaAction({
          id: editingTestCase.ID,
          category_id: Number(testCaseData.category),
          high_level_scenario: testCaseData.highLevelScenario || null,
          pre_conditions: testCaseData.preConditions || null,
          description: testCaseData.description || null,
          expected_result: testCaseData.expectedResult || null,
          test_case_status: testCaseData.status ? "Active" : "Inactive",
        })
      );
    } else {
      dispatch(
        createTestCaseSagaAction({
          category_id: Number(testCaseData.category),
          high_level_scenario: testCaseData.highLevelScenario || null,
          pre_conditions: testCaseData.preConditions || null,
          description: testCaseData.description || null,
          expected_result: testCaseData.expectedResult || null,
          test_case_status: testCaseData.status ? "Active" : "Inactive",
        })
      );
    }
    // Modal will show success state, then close itself
  };

  const handleViewClick = (
    testCase: TestCaseType,
    type:
      | "precondition"
      | "description"
      | "expectedResult"
  ) => {
    setSelectedTestCase(testCase);
    setViewModalType(type);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedTestCase(null);
  };

  // Check scroll position
  React.useEffect(() => {
    const checkScroll = () => {
      const el = catsScrollRef.current;
      if (!el) return;
      setAtStart(el.scrollLeft <= 0);
      setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth);
    };

    const el = catsScrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      checkScroll(); // Initial check
      return () => el.removeEventListener("scroll", checkScroll);
    }
  }, [categoriesWithCounts]);

  const scrollByAmount = (px: number) => {
    const el = catsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: px, behavior: "smooth" });
  };

  return (
    <>
      <div className="test-case-management-content">
        {status.categoriesLoading || status.testCasesLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "50px",
            }}
          >
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : (
          <>
            {categoriesWithCounts.length === 0 ? (
              /* Empty State */
              <>
                <div className="empty-state-header">
                  <Typography className="empty-state-text">
                    No Test Case have been created yet. Please create a category
                    before adding Test Cases.
                  </Typography>
                </div>
                <div className="empty-state-button-container">
                  <Button
                    variant="primary"
                    label="Add test case"
                    icon={<Iconography icon="plus" />}
                    onClick={handleOpenModal}
                    disabled={readOnly}
                  />
                </div>
                <div className="empty-state">
                  <img src={partnerValidationImage} alt="No test cases" />
                </div>
              </>
            ) : (
              <>
                <div className="tcm-header">
                  <Typography variant="h6" className="tcm-title">
                    Categories
                  </Typography>
                  <Button
                    variant="primary"
                    label="Add test case"
                    icon={<Iconography icon="plus" />}
                    onClick={handleOpenModal}
                    disabled={readOnly}
                  />
                </div>

                {/* Categories Scroll Container */}
                <section className="gdl-categories-row" aria-label="Categories">
                  {/* Left arrow */}
                  <Button
                    appearance="outlined"
                    disabled={atStart}
                    className="gdl-cat-scroll-btn gdl-cat-scroll-btn--prev"
                    onClick={() => scrollByAmount(-320)}
                    aria-label="Scroll categories left"
                    label={
                      <Iconography
                        icon="angleLeft"
                        size={IconographySizes.Small}
                      />
                    }
                  />

                  {/* Category pills */}
                  <div
                    ref={catsScrollRef}
                    className="gdl-cat-scroll"
                    role="listbox"
                  >
                    {categoriesWithCounts.map((category) => (
                      <button
                        key={category.ID}
                        className={`gdl-cat-pill ${
                          selectedCategory === category.ID
                            ? "gdl-cat-pill--selected"
                            : ""
                        }`}
                        onClick={() => setSelectedCategory(category.ID)}
                        aria-pressed={selectedCategory === category.ID}
                      >
                        <div className="gdl-cat__content">
                          <span className="gdl-cat-pill__title">
                            {category.category_name}
                          </span>
                          <span className="gdl-cat-pill__count">
                            {category.testCaseCount} Test Cases
                          </span>
                        </div>

                        {selectedCategory === category.ID ? (
                          <Iconography
                            icon="circleCheck"
                            size={IconographySizes.Small}
                            className="gdl-cat-pill__icon"
                          />
                        ) : (
                          <div className="gdl-cat-pill__radio" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Right arrow */}
                  <Button
                    appearance="outlined"
                    disabled={atEnd}
                    className="gdl-cat-scroll-btn gdl-cat-scroll-btn--next"
                    onClick={() => scrollByAmount(320)}
                    aria-label="Scroll categories right"
                    label={
                      <Iconography
                        icon="angleRight"
                        size={IconographySizes.Small}
                      />
                    }
                  />
                </section>

                {/* Test Cases Section */}
                <div className="test-cases-section">
                  <Typography variant="h6" className="section-title">
                    Test Cases
                  </Typography>

                  <TableContainer
                    component={Paper}
                    className="test-cases-table"
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Test Case ID</TableCell>
                          <TableCell>High level Scenarios</TableCell>
                          <TableCell>Precondition</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Expected Result</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedTestCases.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No test cases found
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedTestCases.map((testCase) => (
                            <TableRow key={testCase.ID}>
                              <TableCell>{testCase.test_case_id}</TableCell>
                              <TableCell>
                                {testCase.high_level_scenario || "N/A"}
                              </TableCell>
                              <TableCell>
                                <button
                                  type="button"
                                  className="view-link"
                                  onClick={() => {
                                    handleViewClick(testCase, "precondition");
                                  }}
                                >
                                  View
                                </button>
                              </TableCell>
                              <TableCell>
                                <button
                                  type="button"
                                  className="view-link"
                                  onClick={() => {
                                    handleViewClick(testCase, "description");
                                  }}
                                >
                                  View
                                </button>
                              </TableCell>
                              <TableCell>
                                <button
                                  type="button"
                                  className="view-link"
                                  onClick={() => {
                                    handleViewClick(testCase, "expectedResult");
                                  }}
                                >
                                  View
                                </button>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    testCase.test_case_status === "Active" ||
                                    testCase.test_case_status === "active"
                                      ? "Active"
                                      : "Inactive"
                                  }
                                  className={`status-chip ${(
                                    testCase.test_case_status || ""
                                  ).toLowerCase()}`}
                                />
                              </TableCell>
                              <TableCell sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="flat"
                                  icon={<Iconography icon="pen" />}
                                  onClick={() => handleEditTestCase(testCase)}
                                  disabled={readOnly}
                                />
                                <Button
                                  variant="flat"
                                  icon={<Iconography icon="trashCan" />}
                                  onClick={() => handleDeleteClick(testCase.ID)}
                                  disabled={readOnly}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalRecords > 0 && (
                    <div
                      className="hid-pagination-container"
                      style={{ marginTop: "20px" }}
                    >
                      <Pagination
                        currentPageNumber={currentPageNumber}
                        totalRecords={totalRecords}
                        pageSize={pageSize}
                        onPageNumberClick={handlePageChange}
                        onPageSizesClick={onPageSizesClick}
                        pageSizes={[
                          { label: "10", value: "10" },
                          { label: "20", value: "20" },
                          { label: "50", value: "50" },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <AddTestCaseModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTestCase}
        editData={editingTestCase}
      />
      {isDeleteModalOpen && (
        <DeleteModal
          setDeleteOpen={setIsDeleteModalOpen}
          onDeleteButtonClick={handleDeleteConfirm}
          deleteButtonLoading={status.deleteTestCaseLoading}
          optionalHeader="Delete Test Case"
          message="Are you sure you want to delete this test case? This action cannot be undone."
        />
      )}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 500, fontSize: "20px" }}>
            {viewModalType === "precondition"
              ? "Precondition"
              : viewModalType === "description"
              ? "Description"
              : "Expected Result"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Iconography icon="xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTestCase && (
            <Box sx={{ py: 2 }}>
              <Typography
                sx={{ fontSize: "16px", color: "#111827", lineHeight: 1.6 }}
              >
                {viewModalType === "precondition"
                  ? selectedTestCase.pre_conditions || "N/A"
                  : viewModalType === "description"
                  ? selectedTestCase.description || "N/A"
                  : selectedTestCase.expected_result || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TestCaseManagement;
